import { jsPDF } from "jspdf";

const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
let BRAND = { name: "FLYTRIP", primary: "#1B1F5B", secondary: "#F5B32B" };
export const setPdfBrand = (b) => { BRAND = { ...BRAND, ...b }; };

const INR = (n) => "Rs. " + Number(n || 0).toLocaleString("en-IN");

// Generates and downloads a branded FLYTRIP tax invoice PDF from the
// /api/invoices/{id} payload. Pure client-side, no server render needed.
export function downloadInvoicePdf(inv) {
  const b = inv.booking || {};
  const seller = inv.seller || {};
  const amt = inv.amount || {};
  if (inv.brand && !inv.brand.is_platform) setPdfBrand({ name: inv.brand.name, primary: inv.brand.primary_color, secondary: inv.brand.secondary_color });
  const d = new jsPDF({ unit: "pt", format: "a4" });
  const W = d.internal.pageSize.getWidth();
  const navy = rgb(BRAND.primary);
  const teal = [14, 124, 107];
  const gold = rgb(BRAND.secondary);

  // Header band
  d.setFillColor(...navy);
  d.rect(0, 0, W, 90, "F");
  d.setFillColor(...gold);
  d.rect(40, 30, 30, 30, "F");
  d.setTextColor(...navy);
  d.setFont("helvetica", "bold");
  d.setFontSize(20);
  d.text(BRAND.name.charAt(0).toUpperCase(), 50, 52);
  d.setTextColor(255, 255, 255);
  d.setFontSize(22);
  d.text(BRAND.name, 82, 48);
  d.setFont("helvetica", "normal");
  d.setFontSize(9);
  d.text("Tax Invoice / Booking Receipt", 82, 66);
  d.setFontSize(10);
  d.text(inv.invoiceNo || "INV", W - 40, 45, { align: "right" });
  d.setFontSize(8);
  const dt = (b.created_at || "").slice(0, 10);
  d.text("Date: " + dt, W - 40, 62, { align: "right" });

  // Seller block
  let y = 120;
  d.setTextColor(...navy);
  d.setFont("helvetica", "bold");
  d.setFontSize(11);
  d.text(seller.name || BRAND.name, 40, y);
  d.setFont("helvetica", "normal");
  d.setFontSize(9);
  d.setTextColor(90, 90, 90);
  d.text("GSTIN: " + (seller.gstin || "-"), 40, y + 15);
  d.text("Email: " + (seller.email || "support@flytrip.in"), 40, y + 29);

  // Booking summary box
  y += 60;
  d.setDrawColor(230, 230, 230);
  d.setFillColor(248, 250, 252);
  d.roundedRect(40, y, W - 80, 118, 8, 8, "FD");
  d.setTextColor(...teal);
  d.setFont("helvetica", "bold");
  d.setFontSize(10);
  d.text("BOOKING DETAILS", 56, y + 22);
  d.setTextColor(50, 50, 50);
  d.setFont("helvetica", "normal");
  d.setFontSize(9);
  const rows = [
    ["Booking Ref (PNR)", b.pnr || "-"],
    ["Type", (b.type || "flight").toUpperCase()],
    ["Route / Hotel", b.route || "-"],
    ["Travel Date", b.date || "-"],
    ["Passenger", (b.passenger && b.passenger.name) || b.user_email || "-"],
    ["Guests / Pax", String(b.pax ?? "-")],
    ["Status", b.status || "Confirmed"],
  ];
  let ry = y + 40;
  rows.forEach(([k, v], i) => {
    const col = i % 2 === 0 ? 56 : W / 2 + 10;
    if (i % 2 === 0 && i > 0) ry += 22;
    d.setTextColor(140, 140, 140);
    d.text(k, col, ry);
    d.setTextColor(30, 30, 30);
    d.setFont("helvetica", "bold");
    d.text(String(v), col, ry + 12);
    d.setFont("helvetica", "normal");
  });

  // Amount table
  y += 140;
  d.setFont("helvetica", "bold");
  d.setTextColor(...navy);
  d.setFontSize(10);
  d.text("AMOUNT", 40, y);
  y += 12;
  d.setDrawColor(230, 230, 230);
  const line = (label, val, bold) => {
    d.setFont("helvetica", bold ? "bold" : "normal");
    d.setFontSize(bold ? 12 : 10);
    d.setTextColor(bold ? navy[0] : 70, bold ? navy[1] : 70, bold ? navy[2] : 70);
    d.text(label, 40, y);
    d.text(INR(val), W - 40, y, { align: "right" });
    y += bold ? 6 : 20;
  };
  line("Base Fare", amt.base);
  line("Taxes & Fees (GST)", amt.gst);
  d.line(40, y, W - 40, y);
  y += 22;
  line("Total Paid", amt.total, true);

  // Footer
  d.setFontSize(8);
  d.setTextColor(150, 150, 150);
  d.text("This is a computer-generated invoice. Thank you for booking with " + BRAND.name + ".", 40, 800);

  d.save(`${inv.invoiceNo || "invoice"}.pdf`);
}

// Branded payment receipt from a booking object (client-side, no server call).
export function downloadReceiptPdf(booking, opts = {}) {
  const b = booking || {};
  const total = opts.total ?? b.total ?? 0;
  const discount = opts.discount ?? 0;
  const paymentId = opts.paymentId || b.razorpay_payment_id || "";
  const d = new jsPDF({ unit: "pt", format: "a4" });
  const W = d.internal.pageSize.getWidth();
  const navy = rgb(BRAND.primary);
  const gold = rgb(BRAND.secondary);
  const teal = [14, 124, 107];

  d.setFillColor(...navy);
  d.rect(0, 0, W, 90, "F");
  d.setFillColor(...gold);
  d.rect(40, 30, 30, 30, "F");
  d.setTextColor(...navy);
  d.setFont("helvetica", "bold");
  d.setFontSize(20);
  d.text(BRAND.name.charAt(0).toUpperCase(), 50, 52);
  d.setTextColor(255, 255, 255);
  d.setFontSize(22);
  d.text(BRAND.name, 82, 48);
  d.setFont("helvetica", "normal");
  d.setFontSize(9);
  d.text("Payment Receipt", 82, 66);
  d.setFontSize(9);
  d.text("Receipt: " + (b.pnr || "-"), W - 40, 45, { align: "right" });
  d.text("Date: " + (b.created_at || "").slice(0, 10), W - 40, 62, { align: "right" });

  let y = 130;
  d.setFillColor(240, 253, 244);
  d.setDrawColor(...teal);
  d.roundedRect(40, y - 24, W - 80, 40, 8, 8, "FD");
  d.setTextColor(...teal);
  d.setFont("helvetica", "bold");
  d.setFontSize(12);
  d.text("PAYMENT SUCCESSFUL", 56, y);

  y += 46;
  const rows = [
    ["Booking Ref (PNR)", b.pnr || "-"],
    ["Type", (b.type || "flight").toUpperCase()],
    ["Route / Hotel", b.route || "-"],
    ["Travel Date", b.date || "-"],
    ["Passenger", (b.passenger && b.passenger.name) || b.user_email || "-"],
    ["Payment Method", (b.payment_method || "razorpay").toUpperCase()],
    ["Payment ID", paymentId || "-"],
    ["Status", b.status || "Confirmed"],
  ];
  d.setFontSize(9);
  rows.forEach(([k, v]) => {
    d.setTextColor(140, 140, 140);
    d.setFont("helvetica", "normal");
    d.text(k, 56, y);
    d.setTextColor(30, 30, 30);
    d.setFont("helvetica", "bold");
    d.text(String(v), 220, y);
    y += 24;
  });

  y += 10;
  d.setDrawColor(230, 230, 230);
  d.line(40, y, W - 40, y);
  y += 26;
  if (discount) {
    d.setFont("helvetica", "normal");
    d.setFontSize(10);
    d.setTextColor(70, 70, 70);
    d.text("Combo Discount", 40, y);
    d.text("- " + INR(discount), W - 40, y, { align: "right" });
    y += 22;
  }
  d.setFont("helvetica", "bold");
  d.setFontSize(13);
  d.setTextColor(...navy);
  d.text("Total Paid", 40, y);
  d.text(INR(total), W - 40, y, { align: "right" });

  d.setFontSize(8);
  d.setTextColor(150, 150, 150);
  d.text("This is a computer-generated receipt. Thank you for booking with " + BRAND.name + ".", 40, 800);
  d.save(`${BRAND.name.replace(/\W+/g, "_")}_Receipt_${b.pnr || "booking"}.pdf`);
}
